import i18n from "../i18n";

/**
 * Resizes an image file to a maximum width while maintaining aspect ratio
 * @param {File} file - The image file to resize
 * @param {number} maxWidth - Maximum width in pixels (default: 1080)
 * @param {number} quality - Image quality for output image (0-1, default: 0.85)
 * @param {string} outputType - Output mime type (default: image/webp)
 * @returns {Promise<File>} - Resized image file
 */
export const resizeImage = (file, maxWidth = 1080, quality = 0.85, outputType = 'image/webp') => {
  return new Promise((resolve, reject) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      reject(new Error(i18n.t("fileIsNotImage")));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;

        // Only resize if image is wider than maxWidth
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');

        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error(i18n.t("canvasToBlobFailed")));
              return;
            }

            // Use output extension by mime type
            const extensionMap = {
              'image/webp': 'webp',
              'image/jpeg': 'jpg',
              'image/png': 'png',
            };

            const currentName = file.name || 'image';
            const baseName = currentName.replace(/\.[^/.]+$/, '');
            const targetExtension = extensionMap[outputType] || (outputType.split('/')[1] || 'webp');
            const outputFileName = `${baseName}.${targetExtension}`;

            // Create new file from blob with output filename and type
            const resizedFile = new File([blob], outputFileName, {
              type: outputType,
              lastModified: Date.now(),
            });

            resolve(resizedFile);
          },
          outputType,
          quality
        );
      };

      img.onerror = () => {
        reject(new Error(i18n.t("imageLoadingFailed")));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error(i18n.t("fileReadingFailed")));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Resizes multiple image files
 * @param {File[]} files - Array of image files to resize
 * @param {number} maxWidth - Maximum width in pixels (default: 1080)
 * @param {number} quality - Image quality for output image (0-1, default: 0.85)
 * @param {string} outputType - Output mime type (default: image/webp)
 * @returns {Promise<File[]>} - Array of resized image files
 */
export const resizeImages = async (files, maxWidth = 1080, quality = 0.85, outputType = 'image/webp') => {
  const resizePromises = files.map(file => resizeImage(file, maxWidth, quality, outputType));
  return Promise.all(resizePromises);
};
